// ============================================================
// PLUMME 3 — Prosjekter og timeføring · domenelogikk
//
// Databasefri. Fila eier normalisering, uke-/månedsaggregering, låsing og
// fakturagrunnlaget som timer omsettes til fakturalinjer gjennom. Backend
// (`src/services/backend.jsx`) gjør lagring og fakturaopprettelse, sidene under
// `src/pages/prosjekter*.jsx` gjør presentasjon — regnereglene ligger her.
//
// Regnskapsregler som er styrende for fakturagrunnlaget:
//  - Timer på en aktivitet med fakturering «aldri» er interne og skal aldri
//    havne på en faktura (ferie, egenutvikling, garantiarbeid).
//  - Er aktiviteten koblet til et produkt, arver linja produktets mva-behandling
//    og salgskonto. Da bokføres timene på samme inntektskonto som resten av
//    salget av den tjenesten, i stedet for å havne på en generisk 3000-konto.
//  - Uten produkt er timer en tjeneste: standard sats og salgskonto 3020.
//  - En timeoppføring kan bare faktureres én gang. `invoiceStatus` er sannheten,
//    og settes av backend når fakturautkastet faktisk er lagret.
// ============================================================
const PlummeProjectTimeTracking = (function projectTimeTracking() {
  const PROJECT_COLORS = ['#5B5BD6', '#1B1B3A', '#1FA97D', '#F4D35E', '#E54D9B', '#E5844D'];
  const BILLING_MODES = ['always', 'ask', 'never'];
  // Ingen fakturagrunnlag uten produkt skal bokføres på varekonto. Timer er
  // tjenestesalg, og 3020 er standardkontoen for tjenester med høy sats.
  const DEFAULT_SERVICE_ACCOUNT = '3020';
  const MAX_HOURS_PER_ENTRY = 24;
  const MS_PER_DAY = 86400000;

  // Valideringstekstene er brukervendte. `t` finnes i appen, men ikke i
  // enhetstestene av denne fila — derfor fallback i stedet for hard avhengighet.
  const tr = (key, fallback) => (typeof globalThis.t === 'function' ? globalThis.t(key, fallback) : fallback);

  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const text = (value) => String(value == null ? '' : value).trim();
  const num = (value, fallback = 0) => {
    const parsed = Number(String(value == null ? '' : value).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const pad2 = (value) => String(value).padStart(2, '0');
  const nowIso = () => new Date().toISOString();

  // Datoer holdes på kl. 12 lokal tid. Midnatt + sommertid gir av og til
  // «forrige dag» når man legger til døgn, og da hopper hele uketabellen.
  const parseDate = (value) => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
    const key = text(value).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
    const parsed = new Date(`${key}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
  const isoDate = (value) => {
    const date = parseDate(value);
    return date ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` : '';
  };
  const addDays = (value, days) => {
    const date = parseDate(value);
    if (!date) return '';
    date.setDate(date.getDate() + Number(days || 0));
    return isoDate(date);
  };
  const mondayOf = (value) => {
    const date = parseDate(value);
    if (!date) return '';
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    return isoDate(date);
  };
  // ISO-8601 ukenummer: uke 1 er uka som inneholder årets første torsdag.
  const isoWeekNumber = (value) => {
    const date = parseDate(value);
    if (!date) return 0;
    const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    target.setUTCDate(target.getUTCDate() - ((target.getUTCDay() + 6) % 7) + 3);
    const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
    firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
    return 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * MS_PER_DAY));
  };
  const weekDays = (value) => {
    const monday = mondayOf(value);
    return monday ? Array.from({ length: 7 }, (_, index) => addDays(monday, index)) : [];
  };
  const monthRange = (year, month) => {
    const from = `${year}-${pad2(month)}-01`;
    return { from, to: isoDate(new Date(Number(year), Number(month), 0, 12)) };
  };

  // Timer lagres som desimaltall, avrundet til nærmeste minutt. Uten
  // avrundingen gir 0.1 + 0.2 fakturagrunnlag med 18 desimaler.
  const roundHours = (value) => Math.round(num(value) * 60) / 60;
  const formatHours = (value) => {
    const total = Math.max(0, Math.round(num(value) * 60));
    const parts = { h: Math.floor(total / 60), m: total % 60 };
    return typeof globalThis.t === 'function' ? globalThis.t('common.hoursMinutes', '{h}t {m}m', parts) : `${parts.h}t ${parts.m}m`;
  };

  const normalizeBilling = (value) => {
    const key = text(value).toLowerCase();
    if (key === 'never' || key === 'aldri') return 'never';
    if (key === 'ask' || key === 'spør' || key === 'spor') return 'ask';
    return 'always';
  };
  const normalizeColor = (value, fallback) => (/^#[0-9a-f]{6}$/i.test(text(value)) ? text(value) : (fallback || PROJECT_COLORS[0]));

  const normalizeProject = (raw = {}, existing = {}) => {
    const name = text(raw.name) || text(existing.name);
    if (!name) throw new Error(tr('pj.validation.projectName', 'Prosjektet må ha et navn.'));
    const startDate = isoDate(raw.startDate ?? existing.startDate) || isoDate(new Date());
    const endDate = isoDate(raw.endDate ?? existing.endDate);
    if (endDate && endDate < startDate) throw new Error(tr('pj.validation.endBeforeStart', 'Sluttdato kan ikke være før startdato.'));
    return {
      ...existing,
      id: text(raw.id) || text(existing.id) || `pj${Date.now()}${Math.round(Math.random() * 1000)}`,
      projectNumber: Math.max(0, Math.round(num(raw.projectNumber ?? existing.projectNumber))) || 0,
      name,
      customerId: text(raw.customerId ?? existing.customerId),
      customerName: text(raw.customerName ?? existing.customerName),
      contactPerson: text(raw.contactPerson ?? existing.contactPerson),
      defaultActivityId: text(raw.defaultActivityId ?? existing.defaultActivityId),
      startDate,
      endDate,
      description: text(raw.description ?? existing.description),
      color: normalizeColor(raw.color ?? existing.color, PROJECT_COLORS[0]),
      status: (raw.status ?? existing.status) === 'closed' || raw.closed === true ? 'closed' : 'active',
      createdAt: existing.createdAt || nowIso(),
      updatedAt: nowIso(),
    };
  };

  const normalizeActivity = (raw = {}, existing = {}) => {
    const name = text(raw.name) || text(existing.name);
    if (!name) throw new Error(tr('pj.validation.activityName', 'Aktiviteten må ha et navn.'));
    const projectId = text(raw.projectId ?? existing.projectId);
    if (!projectId) throw new Error(tr('pj.validation.activityProject', 'Aktiviteten må høre til et prosjekt.'));
    const unitPrice = Math.max(0, num(raw.unitPrice ?? existing.unitPrice));
    return {
      ...existing,
      id: text(raw.id) || text(existing.id) || `pa${Date.now()}${Math.round(Math.random() * 1000)}`,
      projectId,
      name,
      productId: text(raw.productId ?? existing.productId),
      unitPrice,
      billing: normalizeBilling(raw.billing ?? existing.billing),
      createdAt: existing.createdAt || nowIso(),
      updatedAt: nowIso(),
    };
  };

  const normalizeTimeEntry = (raw = {}, existing = {}) => {
    const date = isoDate(raw.date ?? existing.date);
    if (!date) throw new Error(tr('pj.validation.entryDate', 'Timeoppføringen må ha en dato.'));
    const hours = roundHours(raw.hours ?? existing.hours);
    if (!(hours > 0)) throw new Error(tr('pj.validation.entryHours', 'Antall timer må være større enn 0.'));
    if (hours > MAX_HOURS_PER_ENTRY) throw new Error(tr('pj.validation.entryHoursMax', 'Én registrering kan ikke være mer enn 24 timer.'));
    const invoiceStatus = (raw.invoiceStatus ?? existing.invoiceStatus) === 'invoiced' ? 'invoiced' : 'open';
    return {
      ...existing,
      id: text(raw.id) || text(existing.id) || `te${Date.now()}${Math.round(Math.random() * 1000)}`,
      date,
      projectId: text(raw.projectId ?? existing.projectId),
      activityId: text(raw.activityId ?? existing.activityId),
      hours,
      note: text(raw.note ?? existing.note),
      personName: text(raw.personName ?? existing.personName),
      // Timer fort fra ansattportalen baerer ansattkortets id, ikke bare et navn.
      // Uten den kunne ingen si hvem timen tilhorer naar to ansatte heter det samme.
      employeeId: text(raw.employeeId ?? existing.employeeId),
      invoiceStatus,
      invoiceId: invoiceStatus === 'invoiced' ? text(raw.invoiceId ?? existing.invoiceId) : '',
      invoicedAt: invoiceStatus === 'invoiced' ? (text(raw.invoicedAt ?? existing.invoicedAt) || nowIso()) : '',
      createdAt: existing.createdAt || nowIso(),
      updatedAt: nowIso(),
    };
  };

  const normalizeSettings = (raw = {}) => {
    const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    const seen = new Set();
    return {
      lockedThrough: isoDate(source.lockedThrough),
      pinnedRows: safeArray(source.pinnedRows).map((row, index) => ({
        id: text(row?.id) || `pr${index}`,
        projectId: text(row?.projectId),
        activityId: text(row?.activityId),
      })).filter((row) => {
        const key = `${row.projectId}|${row.activityId}`;
        if (!row.projectId || !row.activityId || seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, 40),
    };
  };

  const nextProjectNumber = (projects) =>
    safeArray(projects).reduce((highest, project) => Math.max(highest, Math.round(num(project?.projectNumber))), 0) + 1;

  const isDateLocked = (settings, date) => {
    const lockedThrough = normalizeSettings(settings).lockedThrough;
    const key = isoDate(date);
    return Boolean(lockedThrough && key && key <= lockedThrough);
  };

  // Én normalisert lesemodell for alle fanene. Sidene skal aldri slå opp i
  // rå-state selv — da havner vi fort med to ulike sannheter om hva som er låst
  // eller allerede fakturert.
  const buildModel = (state = {}) => {
    const projects = safeArray(state.projects).map((project) => {
      try { return normalizeProject(project, project); } catch (error) { return null; }
    }).filter(Boolean).sort((a, b) => (a.projectNumber - b.projectNumber) || a.name.localeCompare(b.name, 'nb'));
    const projectIds = new Set(projects.map((project) => project.id));
    const activities = safeArray(state.projectActivities).map((activity) => {
      try { return normalizeActivity(activity, activity); } catch (error) { return null; }
    }).filter((activity) => activity && projectIds.has(activity.projectId))
      .sort((a, b) => a.name.localeCompare(b.name, 'nb'));
    const activityIds = new Set(activities.map((activity) => activity.id));
    const entries = safeArray(state.timeEntries).map((entry) => {
      try { return normalizeTimeEntry(entry, entry); } catch (error) { return null; }
    }).filter((entry) => entry && projectIds.has(entry.projectId) && activityIds.has(entry.activityId))
      .sort((a, b) => b.date.localeCompare(a.date));
    const settings = normalizeSettings(state.timeTrackingSettings);
    const projectsById = new Map(projects.map((project) => [project.id, project]));
    const activitiesById = new Map(activities.map((activity) => [activity.id, activity]));
    return {
      projects,
      activities,
      entries,
      settings,
      projectsById,
      activitiesById,
      // Rader man har festet i uketabellen skal forsvinne når prosjektet eller
      // aktiviteten slettes, ikke bli hengende igjen som tomme linjer.
      pinnedRows: settings.pinnedRows.filter((row) => projectsById.has(row.projectId) && activitiesById.has(row.activityId)),
    };
  };

  const decorateEntry = (entry, model) => ({
    ...entry,
    project: model.projectsById.get(entry.projectId) || null,
    activity: model.activitiesById.get(entry.activityId) || null,
  });

  const entriesInRange = (entries, from, to) => {
    const fromKey = isoDate(from);
    const toKey = isoDate(to);
    return safeArray(entries).filter((entry) =>
      (!fromKey || entry.date >= fromKey) && (!toKey || entry.date <= toKey));
  };
  const totalHours = (entries) => roundHours(safeArray(entries).reduce((sum, entry) => sum + num(entry?.hours), 0));
  const hoursByDate = (entries) => safeArray(entries).reduce((map, entry) => {
    map[entry.date] = roundHours(num(map[entry.date]) + num(entry.hours));
    return map;
  }, {});

  // Rapportfilteret. `billing` filtrerer på aktivitetens faktureringsregel,
  // `invoiced` på om timene faktisk er tatt med på en faktura.
  const filterEntries = (model, filters = {}) => {
    const { projectId = '', activityId = '', from = '', to = '', billing = 'all', invoiced = 'all', search = '' } = filters;
    const query = text(search).toLowerCase();
    return entriesInRange(model.entries, from, to)
      .filter((entry) => !projectId || entry.projectId === projectId)
      .filter((entry) => !activityId || entry.activityId === activityId)
      .map((entry) => decorateEntry(entry, model))
      .filter((entry) => {
        if (billing === 'billable') return entry.activity && entry.activity.billing !== 'never';
        if (billing === 'nonBillable') return !entry.activity || entry.activity.billing === 'never';
        return true;
      })
      .filter((entry) => {
        if (invoiced === 'invoiced') return entry.invoiceStatus === 'invoiced';
        if (invoiced === 'open') return entry.invoiceStatus !== 'invoiced';
        return true;
      })
      .filter((entry) => !query || [entry.note, entry.project?.name, entry.activity?.name].join(' ').toLowerCase().includes(query));
  };

  // Timer som venter på fakturering, gruppert per prosjekt. Interne aktiviteter
  // og prosjekter uten kunde holdes utenfor: de kan ikke bli en faktura.
  const buildInvoiceBasisGroups = (model, { search = '' } = {}) => {
    const query = text(search).toLowerCase();
    return model.projects
      .filter((project) => project.customerId || project.customerName)
      .map((project) => {
        const entries = model.entries
          .filter((entry) => entry.projectId === project.id && entry.invoiceStatus !== 'invoiced')
          .map((entry) => decorateEntry(entry, model))
          .filter((entry) => entry.activity && entry.activity.billing !== 'never')
          .sort((a, b) => a.date.localeCompare(b.date));
        const dates = entries.map((entry) => entry.date);
        return {
          project,
          entries,
          hours: totalHours(entries),
          from: dates[0] || '',
          to: dates[dates.length - 1] || '',
        };
      })
      .filter((group) => group.entries.length)
      .filter((group) => !query || `${group.project.name} ${group.project.customerName}`.toLowerCase().includes(query));
  };

  /**
   * Fakturagrunnlaget: timer inn, fakturalinjer ut.
   *
   * Én linje per aktivitet, med timene summert. Produktet på aktiviteten
   * bestemmer mva-behandling og salgskonto, slik at timene bokføres på samme
   * inntektskonto som resten av salget av den tjenesten. Uten produkt faller
   * linja tilbake på tjenestekontoen og selskapets standardsats.
   */
  const buildInvoiceLines = (entries, { products = [] } = {}) => {
    const productsById = new Map(safeArray(products).map((product) => [text(product?.id), product]));
    const groups = new Map();
    // Kronologisk rekkefolge, uansett hvilken vei kalleren leverte timene.
    // Linjekommentaren skal leses som en fortelling om hva som ble gjort.
    safeArray(entries).slice().sort((a, b) => String(a?.date || '').localeCompare(String(b?.date || ''))).forEach((entry) => {
      const activity = entry.activity;
      if (!activity || activity.billing === 'never') return;
      const hours = roundHours(entry.hours);
      if (!(hours > 0)) return;
      const existing = groups.get(activity.id);
      if (existing) {
        existing.quantity = roundHours(existing.quantity + hours);
        existing.entryIds.push(entry.id);
        existing.notes.push(text(entry.note));
        return;
      }
      const product = productsById.get(text(activity.productId)) || null;
      groups.set(activity.id, {
        activityId: activity.id,
        entryIds: [entry.id],
        notes: [text(entry.note)],
        description: text(product?.name) || activity.name,
        productId: product ? product.id : '',
        productName: text(product?.name),
        itemNumber: text(product?.nr),
        unit: 'timer',
        quantity: hours,
        // Aktivitetens timepris går foran produktprisen: den er satt for dette
        // prosjektet, produktprisen er listeprisen.
        unitPrice: activity.unitPrice > 0 ? activity.unitPrice : num(product?.price),
        vat: product ? (product.vatSelection ?? product.vat) : undefined,
        salesVatCode: text(product?.salesVatCode) || undefined,
        salesAccountCode: text(product?.salesAccountCode) || DEFAULT_SERVICE_ACCOUNT,
        type: 'Tjeneste',
      });
    });
    return Array.from(groups.values()).map((line) => ({
      ...line,
      // Notatene fra timene blir linjekommentar, slik at kunden ser hva timene
      // gikk til uten at hver dag blir en egen fakturalinje.
      note: Array.from(new Set(line.notes.filter(Boolean))).join(' · ').slice(0, 400),
      amount: Math.round(line.quantity * line.unitPrice * 100) / 100,
    })).map(({ notes, ...line }) => line);
  };

  // Timelistevedlegget som kan følge fakturaen. Rene rader, ingen formatering —
  // PDF-en bygges der vedlegget faktisk lages.
  const buildTimesheetRows = (entries) => safeArray(entries)
    .slice()
    // Dato foerst, deretter aktivitet, saa vedlegget ser likt ut hver gang det
    // lages — en kunde som sammenligner to timelister skal se samme rekkefolge.
    .sort((a, b) => a.date.localeCompare(b.date) || text(a.activity?.name).localeCompare(text(b.activity?.name), 'nb'))
    .map((entry) => ({
      date: entry.date,
      activity: text(entry.activity?.name),
      description: text(entry.note),
      hours: roundHours(entry.hours),
    }));

  /**
   * Timerapporten som PDF-nyttelast.
   *
   * Samme kontrakt som resultatregnskap og saldobalanse bruker mot
   * `sendReportPdfEmailHttp` — én seksjon per prosjekt, én rad per registrering
   * — men med timer som enhet i stedet for kroner.
   */
  const buildTimeReportExport = (entries, { title = 'Timerapport', periodLabel = '', year, billableLabel = 'Fakturerbart', totalLabel = 'Sum timer' } = {}) => {
    const rows = safeArray(entries).slice().sort((a, b) => a.date.localeCompare(b.date));
    const byProject = new Map();
    rows.forEach((entry) => {
      const key = text(entry.project?.name) || text(entry.projectId) || '—';
      const section = byProject.get(key) || { title: key, rows: [] };
      const dateLabel = `${entry.date.slice(8, 10)}.${entry.date.slice(5, 7)}.${entry.date.slice(0, 4)}`;
      const parts = [dateLabel, text(entry.activity?.name), text(entry.note)].filter(Boolean);
      section.rows.push({ label: parts.join(' · '), amount: roundHours(entry.hours) });
      byProject.set(key, section);
    });
    const billable = rows.filter((entry) => entry.activity && entry.activity.billing !== 'never');
    return {
      reportType: 'time-report',
      title,
      periodLabel,
      year: Number(year) || Number(String(rows[0]?.date || '').slice(0, 4)) || new Date().getFullYear(),
      valueUnit: 'timer',
      valueDecimals: 2,
      summaryRows: [
        { label: totalLabel, amount: totalHours(rows) },
        { label: billableLabel, amount: totalHours(billable) },
      ],
      sections: Array.from(byProject.values()),
    };
  };

  const validateEntryEditable = (model, entry) => {
    if (!entry) throw new Error(tr('pj.validation.entryNotFound', 'Fant ikke timeoppføringen.'));
    if (entry.invoiceStatus === 'invoiced') throw new Error(tr('pj.validation.entryInvoiced', 'Timeoppføringen er allerede fakturert og kan ikke endres.'));
    if (isDateLocked(model.settings, entry.date)) throw new Error(tr('pj.validation.periodLocked', 'Timeføringen for denne perioden er markert som ferdig.'));
  };

  return {
    PROJECT_COLORS,
    BILLING_MODES,
    DEFAULT_SERVICE_ACCOUNT,
    MAX_HOURS_PER_ENTRY,
    isoDate,
    addDays,
    mondayOf,
    isoWeekNumber,
    weekDays,
    monthRange,
    roundHours,
    formatHours,
    normalizeBilling,
    normalizeProject,
    normalizeActivity,
    normalizeTimeEntry,
    normalizeSettings,
    nextProjectNumber,
    isDateLocked,
    buildModel,
    decorateEntry,
    entriesInRange,
    totalHours,
    hoursByDate,
    filterEntries,
    buildInvoiceBasisGroups,
    buildInvoiceLines,
    buildTimesheetRows,
    buildTimeReportExport,
    validateEntryEditable,
  };
})();

Object.assign(globalThis, { PlummeProjectTimeTracking });
